import React, { useEffect, useState } from 'react';
import { ref, onValue, get, query, orderByChild, equalTo } from 'firebase/database';
import { auth, database } from '../firebaseConfig';

// "Approve email" tasks: client emails waiting for the deal agent / co-agent (or an Admin) to approve.
// Opens a preview with Approve & send, Edit (then send) and Skip; Dismiss on the card skips without opening it. The server makes sure only one decision counts.
const fmtEt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/New_York', dateStyle: 'medium', timeStyle: 'short' }) + ' ET' : '');
const box = { border: '2px solid #D4AF37', borderRadius: 8, padding: '1rem', background: '#FFFBEF', marginBottom: '1.25rem' };
const btn = (dark) => ({ padding: '8px 14px', borderRadius: 6, border: '1px solid #1A1A1A', background: dark ? '#1A1A1A' : '#fff', color: dark ? '#D4AF37' : '#1A1A1A', fontWeight: 700, cursor: 'pointer', marginRight: 8 });

export const usePendingApprovals = (uid, isAdmin) => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!uid) return undefined;
    if (isAdmin) return onValue(query(ref(database, 'crm/emailApprovals'), orderByChild('status'), equalTo('pending')), (s) => setItems(Object.entries(s.val() || {}).map(([id, v]) => ({ id, ...v }))), () => setItems([]));
    return onValue(ref(database, `crm/approvalInbox/${uid}`), async (s) => {
      const ids = Object.keys(s.val() || {});
      const recs = await Promise.all(ids.map((id) => get(ref(database, `crm/emailApprovals/${id}`)).then((r) => (r.exists() ? { id, ...r.val() } : null)).catch(() => null)));
      setItems(recs.filter((r) => r && r.status === 'pending'));
    }, () => setItems([]));
  }, [uid, isAdmin]);
  return items.sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
};

const post = async (body) => {
  const idToken = await auth.currentUser.getIdToken();
  const res = await fetch('/api/email-approval', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` }, body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  return { ...data, httpOk: res.ok };
};

function Preview({ item, onClose }) {
  const [editing, setEditing] = useState(false);
  const [subject, setSubject] = useState(item.subject || '');
  const [html, setHtml] = useState(item.kind === 'blast' ? (item.body || '') : (item.html || ''));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const act = async (decision) => {
    if (decision === 'skip' && !window.confirm('Skip this email? It will not be sent.')) return;
    setBusy(true); setMsg('');
    const edits = editing ? (item.kind === 'blast' ? { subject, body: html } : { subject, html }) : null;
    const r = await post({ id: item.id, decision, edits });
    setBusy(false);
    if (r.ok) { setMsg(r.status === 'skipped' ? 'Skipped. Nothing was sent.' : r.status === 'approved_scheduled' ? 'Approved. It will send at its scheduled time.' : `Sent${r.testMode ? ' (test mode: went to the test inbox)' : ''}.`); setTimeout(onClose, 1200); }
    else setMsg(r.message || r.error || 'Could not complete.');
  };
  const row = (k, v) => v ? <div style={{ fontSize: 14, margin: '2px 0' }}><strong>{k}:</strong> {v}</div> : null;
  return (
    <div role="dialog" aria-label="Approve email" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 10, maxWidth: 760, width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 20 }}>
        <h3 style={{ marginTop: 0 }}>Approve email{item.dealLabel ? ` – ${item.dealLabel}` : ''}</h3>
        {row('To', [].concat(item.to || []).join(', ') + (item.toName ? ` (${item.toName})` : ''))}
        {row('Cc', (item.cc || []).join(', '))}
        {row('From', item.fromDisplay || item.fromName)}
        {row('Reply-to', [item.sender && item.sender.email, ...(item.replyTo || [])].filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', '))}
        {row('When', item.kind === 'blast' ? (item.sendAt ? `Scheduled ${fmtEt(item.sendAt)}` : 'Right after approval') : 'Right after approval')}
        {row('Expires', `${fmtEt(item.expiresAt)} (not sent if nobody approves by then)`)}
        {editing ? (
          <div style={{ marginTop: 10 }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} style={{ width: '100%', padding: 8, boxSizing: 'border-box' }} />
            <label style={{ fontSize: 13, fontWeight: 700 }}>{item.kind === 'blast' ? 'Message ({first name} fills in each name)' : 'Message (HTML)'}</label>
            <textarea value={html} onChange={(e) => setHtml(e.target.value)} rows={14} style={{ width: '100%', padding: 8, boxSizing: 'border-box', fontFamily: 'monospace', fontSize: 12 }} />
          </div>
        ) : (
          <>
            {row('Subject', item.subject)}
            <iframe title="Email preview" sandbox="" srcDoc={item.html || ''} style={{ width: '100%', height: 360, border: '1px solid #ddd', borderRadius: 6, marginTop: 8 }} />
            {item.kind === 'blast' && <div style={{ fontSize: 12, color: '#666' }}>Preview shows the first contact's name; each person gets their own.</div>}
          </>
        )}
        {msg && <div style={{ margin: '10px 0', fontWeight: 700 }}>{msg}</div>}
        <div style={{ marginTop: 14 }}>
          <button type="button" disabled={busy} style={btn(true)} onClick={() => act('approve')}>{busy ? 'Working...' : editing ? 'Send edited email' : 'Approve & send'}</button>
          {!editing && <button type="button" disabled={busy} style={btn(false)} onClick={() => setEditing(true)}>Edit</button>}
          <button type="button" disabled={busy} style={btn(false)} onClick={() => act('skip')}>Skip</button>
          <button type="button" disabled={busy} style={{ ...btn(false), border: 0 }} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function EmailApprovalsPanel({ uid, isAdmin }) {
  const items = usePendingApprovals(uid, isAdmin);
  const [open, setOpen] = useState(null);
  const [gone, setGone] = useState({});
  const [note, setNote] = useState('');
  // Dismiss = the same "skip" decision as in the preview: nothing is sent and the card goes away.
  const dismiss = async (it) => {
    if (!window.confirm('Dismiss this email? It will not be sent.')) return;
    setNote('');
    const r = await post({ id: it.id, decision: 'skip' });
    if (r.ok) setGone((g) => ({ ...g, [it.id]: true }));
    else setNote(r.message || r.error || 'Could not dismiss. Try again.');
  };
  const visible = items.filter((it) => !gone[it.id]);
  if (!visible.length) return null;
  return (
    <div style={box}>
      <div style={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F', marginBottom: 8 }}>Approve email ({visible.length})</div>
      {note && <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>{note}</div>}
      {visible.map((it) => (
        <div key={it.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', borderTop: '1px solid #eee', padding: '8px 0', flexWrap: 'wrap' }}>
          <div style={{ fontSize: 14 }}>
            <strong>{it.kind === 'blast' ? 'Email blast' : 'Approve email'}:</strong> {it.subject}
            <div style={{ fontSize: 12, color: '#666' }}>To {[].concat(it.to || []).join(', ')}{it.dealLabel ? ` · ${it.dealLabel}` : ''} · expires {fmtEt(it.expiresAt)}</div>
          </div>
          <div>
            <button type="button" style={btn(true)} onClick={() => setOpen(it)}>Preview &amp; approve</button>
            <button type="button" style={btn(false)} onClick={() => dismiss(it)}>Dismiss</button>
          </div>
        </div>
      ))}
      {open && <Preview item={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
